<template>
  <div class="page" v-if="can('news.show')">
    <h2 class="accent--text">
      <v-icon color="primary" class="ml-3" large>mdi-newspaper</v-icon>
      إدارة الأخبار
      <back-button />
    </h2>
    <hr class="my-5 header-divider" />

    <!-- RECORD INFO DIALOG -->
    <record-info />

    <!-- FILTERS BOX -->
    <filters-box />

    <!-- UPDATE / NEW RECORD -->
    <div class="mb-3 mt-9 text-left">
      <v-btn
        v-if="can('news.store')"
        color="primary"
        elevation="0"
        to="/news/0"
      >
        إضافة خبر
        <v-icon class="mr-2">mdi-plus</v-icon>
      </v-btn>
    </div>

    <!-- TABLE -->
    <v-data-table
      item-key="id"
      class="elevation-1"
      :loading="table.loading"
      :headers="table.headers"
      :items="records"
      loading-text="جاري تحميل البيانات"
      no-data-text="لا يوجد بيانات"
      disable-filtering
      disable-pagination
      disable-sort
      hide-default-footer
    >
      <!-- AUTHOR -->
      <template v-slot:item.author="{ item }">
        <template v-if="item.author && item.author.length > 0">
          <img
            :src="item.author[0].image"
            width="50px"
            class="mt-3 mb-2 ml-2 rounded-lg"
            style="vertical-align: middle"
          >
          <span>{{ item.author[0].name }}</span>
        </template>
      </template>
      <!-- PUBLISHED -->
      <template v-slot:item.published_date="{ item }">
        <span v-if="item.published" class="cyan--text text--darken-2">
          {{ item.published_date }}
        </span>
        <span v-else class="warning--text text--darken-2">
          غير منشور
        </span>
      </template>
      <!-- CONTROLS -->
      <template v-slot:item.controls="{ item }">
        <v-btn
          v-if="can('news.edit') && !item.published"
          small
          outlined
          color="cyan darken-3"
          class="mx-2"
          @click="publish(item)"
        >
          نشر
        </v-btn>
        <v-btn
          v-else-if="can('news.edit')"
          small
          outlined
          color="deep-orange darken-2"
          class="mx-2"
          @click="hide(item)"
        >
          إخفاء
        </v-btn>

        <v-btn
          small
          icon
          color="info"
          class="mx-2"
          :href="previewLink(item.id)"
          target="_blank"
        >
          <v-icon>mdi-eye</v-icon>
        </v-btn>

        <v-btn
          small
          icon
          color="success"
          class="mx-2"
          :to="`/news/${item.id}`"
          v-if="can('news.edit')"
        >
          <v-icon>mdi-circle-edit-outline</v-icon>
        </v-btn>

        <v-btn
          v-if="can('news.delete')"
          small
          icon
          color="error"
          class="mx-2"
          @click="remove(item)"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- PAGINATION -->
    <div class="text-center mt-7">
      <v-pagination
        total-visible="7"
        circle
        :value="parseInt(filters.page)"
        :length="totalPages"
        @input="$router.replace({ query: { ...$route.query, page: $event } })"
      ></v-pagination>
    </div>
  </div>
</template>


<script>
import FiltersBox from './FiltersBox';
import RecordInfo from './RecordInfo';
import { mapState, mapMutations } from 'vuex';

export default {
  name: 'news',
  components: { FiltersBox, RecordInfo },

  data: function () {
    return {
      table: {
        headers: [
          {
            value: 'id',
            text: 'رقم الخبر'
          },
          {
            value: 'title_text',
            text: 'العنوان'
          },
          {
            value: 'author',
            text: 'المؤلف'
          },
          {
            value: 'published_date',
            text: 'تاريخ النشر'
          },
          {
            value: 'created_at',
            text: 'تاريخ الإضافة'
          },
          {
            value: 'controls',
            text: 'اعدادات'
          }
        ],
        loading: false
      }
    }
  },

  computed: {
    ...mapState('news', ['records', 'filters', 'totalPages']
    )
  },


  watch: {
    '$route': {
      async handler(route) {
        if (!route.query.page) {
          await this.$router.replace({ query: { ...route.query, page: 1 } });
          return;
        }

        this.SET_FILTERS(route.query);
        this.fetchRecords();
      },
      deep: true,
      immediate: true,
    }
  },


  methods: {
    ...mapMutations('news', ['SET_FILTERS', 'SHOW_RECORD_INFO']),

    previewLink(recordId) {
      return `${process.env.VUE_APP_WEBSITE_BASE_URL}/guest-admin/news-unpublished?article_id=${recordId}`
    },

    fetchRecords() {
      this.table.loading = true;

      this.$store.dispatch('news/fetchRecords')
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر جلب البيانات, يرجي التأكد من حالة الشبكة',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });
    },

    fetchRecord(recordId) {
      this.table.loading = true;

      this.$store.dispatch('news/fetchRecord', { id: recordId })
        .then(() => {
          this.SHOW_RECORD_INFO();
        })
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر جلب البيانات, يرجي التأكد من حالة الشبكة',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });
    },

    async remove(selectedRecord) {
      const confirmMessageParams = [
        `حذف الخبر رقم ${selectedRecord.id}!`,
        `هل أنت متأكد من حذف الخبر ${selectedRecord.title_text}؟`,
        { color: 'error' }
      ];
      if (!await this.$root.$confirm(...confirmMessageParams)) return;

      this.table.loading = true;

      this.$store.dispatch('news/delete', { recordId: selectedRecord.id })
        .then(() => {
          this.$bus.$emit('showSnackbar', {
            text: 'تم حذف الخبر بنجاح',
            color: 'success'
          });
        })
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر حذف الخبر',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });
    },

    async publish(selectedRecord) {
      const confirmMessageParams = [
        `نشر الخبر رقم ${selectedRecord.id}!`,
        `هل أنت متأكد من نشر الخبر ${selectedRecord.title_text}؟`,
        { color: 'cyan darken-3' }
      ];
      if (!await this.$root.$confirm(...confirmMessageParams)) return;

      this.table.loading = true;

      this.$store.dispatch('news/publish', { recordId: selectedRecord.id })
        .then(() => {
          this.$bus.$emit('showSnackbar', {
            text: 'تم نشر الخبر بنجاح',
            color: 'success'
          });

          this.fetchRecords();
        })
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر نشر الخبر',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });
    },
    
    async hide(selectedRecord) {
      const confirmMessageParams = [
        `إخفاء الخبر رقم ${selectedRecord.id}!`,
        `هل أنت متأكد من إخفاء الخبر ${selectedRecord.title_text}؟`,
        { color: 'warning darken-2' }
      ];
      if (!await this.$root.$confirm(...confirmMessageParams)) return;

      this.table.loading = true;

      this.$store.dispatch('news/unpublish', { recordId: selectedRecord.id })
        .then(() => {
          this.$bus.$emit('showSnackbar', {
            text: 'تم إخفاء الخبر بنجاح',
            color: 'success'
          });

          this.fetchRecords();
        })
        .catch((error) => {
          this.$bus.$emit('showSnackbar', {
            text: 'تعذر إخفاء الخبر',
            color: 'accent'
          });
        })
        .finally(() => {
          this.table.loading = false;
        });
    }
  }
}
</script>
